import { type ComponentType } from 'react';
import { FileVideoIcon, type IconProps } from '@phosphor-icons/react';
import { Badge } from '@/components/Badge';
import { Popover } from '@/components/Popover';
import { cn } from '@/lib/utils';
import { useDebounce } from '@/hooks/useDebounce';
import { SecurityCameraIconMirrored } from '@/utils/icons';
import { formatCameraEventTime } from '@/utils/date';
import { SourceType } from '@/types/source';
import { SECONDS_IN_DAY } from '@/constants/time';

const MAX_SOURCE_ROWS = 5;
const HOVER_SETTLE_MS = 200;

const SOURCE_TYPE_ICONS: Record<SourceType, ComponentType<IconProps>> = {
    [SourceType.CAMERA]: SecurityCameraIconMirrored,
    [SourceType.FLIR]: SecurityCameraIconMirrored,
    [SourceType.FILE]: FileVideoIcon,
};

export interface ChartTooltipSource {
    id: string;
    name: string;
    type: SourceType;
}

export interface ChartTooltipSourceCount {
    id: string;
    value: number;
}

interface ChartTooltipPoint {
    value?: number;
    startTime?: number;
    endTime?: number;
    sourceCounts?: ChartTooltipSourceCount[];
}

interface ChartTooltipPayloadEntry {
    payload?: ChartTooltipPoint;
}

interface ChartTooltipProps {
    active?: boolean;
    payload?: ChartTooltipPayloadEntry[];
    label?: string;
    valueLabel: string;
    sources?: ChartTooltipSource[];
    titleFormatter?: (value: string) => string;
}

function formatBucketTimeRange(point?: ChartTooltipPoint): string | null {
    if (point?.startTime === undefined || point?.endTime === undefined) {
        return null;
    }
    const durationSeconds = point.endTime - point.startTime + 1;
    if (durationSeconds >= SECONDS_IN_DAY) {
        return null;
    }
    const rangeStart = formatCameraEventTime(point.startTime);
    const rangeEnd = formatCameraEventTime(point.endTime + 1);
    return `${rangeStart} - ${rangeEnd}`;
}

export function ChartTooltip({
    active,
    payload,
    label,
    valueLabel,
    sources,
    titleFormatter,
}: ChartTooltipProps) {
    const hoveredLabel = active && payload?.length ? String(label) : null;
    const settledLabel = useDebounce(hoveredLabel, HOVER_SETTLE_MS);
    if (!hoveredLabel || !payload?.length) {
        return null;
    }
    const settled = settledLabel === hoveredLabel;

    const point = payload[0].payload;
    const title =
        label && titleFormatter ? titleFormatter(String(label)) : label;
    const timeRange = formatBucketTimeRange(point);
    const sourcesById = new Map(
        (sources ?? []).map((source) => [source.id, source]),
    );
    const sourceRows = (point?.sourceCounts ?? [])
        .flatMap((count) => {
            const source = sourcesById.get(count.id);
            return source && count.value > 0
                ? [{ source, value: count.value }]
                : [];
        })
        .sort((first, second) => second.value - first.value)
        .slice(0, MAX_SOURCE_ROWS);

    return (
        <Popover
            title={title}
            subtitle={timeRange ?? undefined}
            className={cn(
                'w-[269px] [font-feature-settings:"ss03"_on,"ss04"_on,"ss06"_on,"ss08"_on,"liga"_off]',
                !settled && 'invisible',
            )}
        >
            <div className="flex flex-col">
                {sourceRows.length > 0 ? (
                    sourceRows.map((row) => {
                        const SourceIcon = SOURCE_TYPE_ICONS[row.source.type];
                        return (
                            <div
                                key={row.source.id}
                                className="flex items-center gap-2 py-2"
                            >
                                <SourceIcon className="w-3.5 h-3.5 text-grey-200 shrink-0" />
                                <span className="flex-1 truncate text-sm text-grey-200">
                                    {row.source.name}
                                </span>
                                <Badge variant="number" value={row.value} />
                            </div>
                        );
                    })
                ) : (
                    <div className="flex items-center gap-2 py-2">
                        <span className="flex-1 truncate text-sm text-grey-200">
                            {valueLabel}
                        </span>
                        <Badge variant="number" value={point?.value ?? 0} />
                    </div>
                )}
            </div>
        </Popover>
    );
}
